import type { UseQueryOptions } from '@tanstack/react-query'

import api from 'src/constants/api'

import useAccount from 'src/hooks/utils/useAccount'
import useQuery from '~/hooks/utils/useQuery'

import moment from 'moment'

const lastDayOfThisMonth = moment().endOf('month')
const startOfAllTime = moment('1970-01-01').startOf('day')

export type TicketPriorityStats = {
  critical: number
  high: number
  medium: number
  low: number
  planning: number
}

export type TicketStateStats = {
  open: number
  awaiting_response: number
  awaiting: number
  closed: number
}

export type TicketStats = TicketPriorityStats | TicketStateStats

export const getTicketStatsQueryKey = (
  accountId: string,
  dateRange: [moment.Moment, moment.Moment],
  groupBy: 'priority' | 'state' = 'state',
  stateFilter: string[] = []
) => {
  return ['ticketStats', accountId, dateRange, groupBy, stateFilter] as const
}

export const fetchTicketStats = async (
  dateRange: [moment.Moment, moment.Moment],
  accountId: string,
  groupBy: 'priority' | 'state' = 'state',
  requester_page: 'azure_deployment_history' | null = null,
  stateFilter: string[] = [],
  signal?: AbortSignal
): Promise<TicketStats | null> => {
  if (!accountId) throw new Error('Account ID is missing')

  try {
    const response = await api.post(
      `/servicenow/account/${accountId}/tickets/stats`,
      {
        start_date: dateRange[0].format('YYYY-MM-DD'),
        end_date: dateRange[1].format('YYYY-MM-DD'),
        group_by: groupBy,
        requester_page: requester_page,
        state_filters: stateFilter
      },
      { signal }
    )
    return response.data as TicketStats
  } catch (error) {
    console.error(error)
    return null
  }
}

type TicketStatsQueryParams<T extends 'priority' | 'state' = 'state'> = {
  dateRange?: [moment.Moment, moment.Moment]
  groupBy?: T
  stateFilter?: string[]
  requester_page?: 'azure_deployment_history' | null
}

const useTicketStatsQuery = <T extends 'priority' | 'state' = 'state'>(
  { dateRange = [startOfAllTime, lastDayOfThisMonth], groupBy = 'state' as T, requester_page = null, stateFilter = [] }: TicketStatsQueryParams<T> = {},
  options?: UseQueryOptions<TicketStats | null>
) => {
  const account = useAccount()

  const result = useQuery(
    getTicketStatsQueryKey(account.id, dateRange, groupBy, stateFilter),
    ({ signal }) => fetchTicketStats(dateRange, account.id, groupBy, requester_page, stateFilter, signal),
    {
      enabled: !!account.id,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      ...options
    }
  )

  return {
    ...result,
    data: result.data as T extends 'priority' ? TicketPriorityStats : TicketStateStats
  }
}

export default useTicketStatsQuery
